import useCreateMarkup from 'components/Dashboard/hooks/useCreateMarkup';
import thousandCommas from 'components/Dashboard/helpers/thousandCommas';
import { CrmContactImage } from 'components/Dashboard/Influence/CrmContacts/CrmContactsTable/CrmContactItem/CrmContactItem';

const RECIPIENTS_TABLE_LIMIT = 20;

// Report widgets can end up rendered inside a TinyMCE iframe (see SpecificUpdateComponent.jsx's
// "click to edit" state), which has no access to the app's compiled stylesheet - these blocks use
// plain tables with inline styles instead of the app's CSS classes so they render consistently
// wherever the baked HTML lands.
const tableStyle = { width: '100%', borderCollapse: 'collapse', border: '1px solid #e5e5e5', marginBottom: '20px' };
const thStyle = {
  textAlign: 'left',
  padding: '8px 12px',
  border: '1px solid #e5e5e5',
  fontSize: '13px',
  backgroundColor: '#00122b',
  color: '#fff',
};
const tdStyle = { padding: '8px 12px', border: '1px solid #e5e5e5', fontSize: '14px', color: '#00122b' };
const labelTdStyle = { ...tdStyle, fontWeight: 700, whiteSpace: 'nowrap' };
const sectionTitleStyle = { fontWeight: 700, marginBottom: '8px' };

const formatPercent = (value) => (typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : 'n/a');

// Subject is used as the report widget's title (see SpecificUpdateController.jsx) and sender now shows
// in the interaction's own metadata line (see CrmInteractionNewContent.jsx), so this is just the body.
const EmailDataBlock = ({ crmEmail }) => {
  const { createMarkup } = useCreateMarkup();
  const { body } = crmEmail ?? {};
  if (!body) return null;
  return (
    <div style={{ marginBottom: '20px' }}>
      <div dangerouslySetInnerHTML={createMarkup(body)} />
    </div>
  );
};

const PerformanceBlock = ({ crmEmail, stats }) => {
  const rows = [
    ['Recipients', crmEmail?.sendRecords?.length ?? 'n/a'],
    ['Open rate', formatPercent(stats?.openRate)],
    ['Unique opens', stats?.uniqueOpens ?? 'n/a'],
    ['Click rate', formatPercent(stats?.clickRate)],
    ['Unique clicks', stats?.uniqueClicks ?? 'n/a'],
    ['Successful deliveries', stats?.totalDeliveries ?? 'n/a'],
    ['Bounces', stats?.totalBounces ?? 'n/a'],
    ['Spam reports', stats?.totalComplaints ?? 'n/a'],
  ];
  const mostClickedLinks = stats?.mostClickedLinks ?? [];
  const contactEngagement = stats?.contactEngagement ?? [];

  return (
    <div style={{ marginBottom: '20px' }}>
      <p style={sectionTitleStyle}>Performance</p>
      <table style={tableStyle}>
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label}>
              <td style={labelTdStyle}>{label}</td>
              <td style={tdStyle}>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {mostClickedLinks.length > 0 && (
        <>
          <p style={sectionTitleStyle}>Top clicked links</p>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Link</th>
                <th style={thStyle}>Clicks</th>
              </tr>
            </thead>
            <tbody>
              {mostClickedLinks.map((link) => (
                <tr key={link.url}>
                  <td style={tdStyle}>{link.url}</td>
                  <td style={tdStyle}>{link.totalCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {contactEngagement.length > 0 && (
        <>
          <p style={sectionTitleStyle}>Most engaged recipients</p>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Recipient</th>
                <th style={thStyle}>Opens</th>
                <th style={thStyle}>Clicks</th>
              </tr>
            </thead>
            <tbody>
              {contactEngagement.map((contact) => (
                <tr key={contact.emailAddress}>
                  <td style={tdStyle}>
                    {contact.fullName} &lt;{contact.emailAddress}&gt;
                  </td>
                  <td style={tdStyle}>{contact.totalOpens}</td>
                  <td style={tdStyle}>{contact.totalClicks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

const RecipientsBlock = ({ crmEmail }) => {
  const sendRecords = crmEmail?.sendRecords ?? [];
  const visibleRecords = sendRecords.slice(0, RECIPIENTS_TABLE_LIMIT);
  const remainingCount = sendRecords.length - visibleRecords.length;

  return (
    <div style={{ marginBottom: '20px' }}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Recipient</th>
            <th style={thStyle}>Status</th>
          </tr>
        </thead>
        <tbody>
          {visibleRecords.map((record) => {
            const status = record.status === 'NotSent' ? 'Not sent' : record.status;
            return (
              <tr key={record.crmEmailSendRecordId ?? record.crmContactId}>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CrmContactImage imageId={record.imageId} imageUrl={record.imageUrl} size={24} />
                    {record.fullName}
                  </div>
                </td>
                <td style={{ ...tdStyle, color: status === 'Bounced' ? '#9b171f' : tdStyle.color }}>{status}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {remainingCount > 0 && (
        <p style={{ fontSize: '13px', color: 'rgba(0,18,43,0.5)' }}>
          +{thousandCommas(remainingCount)} more recipients
        </p>
      )}
    </div>
  );
};

// Fed entirely by data prefetched upstream (SpecificUpdateController) - no internal fetching here, since
// the report widget's height/HTML gets snapshotted once shortly after mount (see useSpecificUpdateCalculations),
// and anything still loading at that point would be silently missing from Preview mode and the exported report.
const EmailCampaignReportBlocks = ({ emailBlocks, emailCampaignData }) => {
  const { crmEmail, stats } = emailCampaignData ?? {};
  return (
    <>
      {emailBlocks?.emailData && <EmailDataBlock crmEmail={crmEmail} />}
      {emailBlocks?.performance && <PerformanceBlock crmEmail={crmEmail} stats={stats} />}
      {emailBlocks?.recipients && <RecipientsBlock crmEmail={crmEmail} />}
    </>
  );
};

export default EmailCampaignReportBlocks;
